<template>
  <div class="emp-settings-root">
    <!-- Sticky Header -->
    <div class="emp-header">
      <div class="d-flex align-center" style="gap: 10px">
        <div class="emp-header-icon">
          <v-icon small color="white">mdi-account-group</v-icon>
        </div>
        <div>
          <div class="text-subtitle-1 font-weight-bold white--text">Mitarbeiter-Einstellungen</div>
          <div class="text-caption" style="color: rgba(255, 255, 255, 0.4)">{{ monthName }} · {{ employees.length }} Mitarbeiter</div>
        </div>
      </div>
      <div class="d-flex align-center" style="gap: 12px">
        <v-chip small :color="readyCount === employees.length ? 'green' : 'orange'" text-color="white" class="font-weight-bold">
          {{ readyCount }}/{{ employees.length }} bereit
        </v-chip>
        <v-btn v-if="readyCount < employees.length" x-small text color="green accent-3" @click="markAllReady" class="text-none">Alle markieren</v-btn>
      </div>
    </div>

    <div class="emp-body">
      <div v-if="loading" class="emp-loading">
        <v-progress-circular indeterminate color="green accent-3" size="56" width="3"></v-progress-circular>
        <p class="mt-4" style="color: rgba(255, 255, 255, 0.4)">Lade Mitarbeiterdaten…</p>
      </div>

      <v-expansion-panels v-else accordion multiple v-model="openPanels" flat class="emp-panels">
        <v-expansion-panel v-for="(emp, index) in employees" :key="emp._id" class="emp-panel">
          <v-expansion-panel-header class="py-3">
            <template v-slot:default="{ open }">
              <v-row no-gutters align="center">
                <v-col cols="auto" class="mr-4">
                  <v-avatar size="48" class="elevation-2">
                    <img :src="emp.picture ? '/img/profilbilder' + emp.picture : '/img/default-avatar.png'" />
                  </v-avatar>
                </v-col>
                <v-col>
                  <div class="d-flex align-center">
                    <div class="text-h6 font-weight-bold white--text mr-2">{{ emp.fullName }}</div>

                    <v-tooltip right v-if="emp.specialAssignments && emp.specialAssignments.length > 0">
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip x-small color="purple lighten-4" text-color="purple darken-3" class="font-weight-bold" v-bind="attrs" v-on="on">
                          <v-icon left x-small>mdi-star</v-icon> {{ emp.specialAssignments.length }}x Spezial
                        </v-chip>
                      </template>
                      <span>Mitarbeiter ist an {{ emp.specialAssignments.length }} Spezialtagen fix eingeteilt.</span>
                    </v-tooltip>
                  </div>

                  <div class="subtitle-2 white--text">
                    <span :class="emp.planningSettings.isReadyForPlanning ? 'success--text' : 'warning--text'">
                      {{ emp.planningSettings.isReadyForPlanning ? "Bereit" : "Noch nicht bestätigt" }}
                    </span>
                    <span class="mx-2">•</span>
                    Vertrag: {{ Math.round(calculateContractTarget(emp)) }}h
                    <span v-if="publicHolidaysInRange.length > 0" class="ml-1" style="font-size: 0.8em">
                      ({{ publicHolidaysInRange.length }} Feiertag{{ publicHolidaysInRange.length > 1 ? "e" : "" }} abgezogen)
                    </span>
                    <span v-if="emp.isApprentice" class="white--text text--lighten-1 ml-1" style="font-size: 0.8em">
                      (Schule bereits abgezogen)
                    </span>
                    <span v-if="calculateVacationHours(emp) > 0" class="orange--text text--lighten-2 ml-1" style="font-size: 0.8em">
                      ({{ Math.round(calculateVacationHours(emp)) }}h Ferien gutgeschrieben)
                    </span>
                  </div>
                </v-col>
                <v-col cols="auto" class="mr-4">
                  <v-chip v-if="emp.planningSettings.isReadyForPlanning" color="success" small label>
                    <v-icon left small>mdi-check</v-icon> Bereit
                  </v-chip>
                  <v-chip v-else color="orange" small label> Ausstehend </v-chip>
                </v-col>
              </v-row>
            </template>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-divider class="mb-4"></v-divider>
            <v-row>
              <v-col cols="12" md="6" class="pr-md-4 border-right-custom">
                <v-card outlined class="pa-3 mb-4 d-flex align-center justify-space-between hover-card">
                  <div>
                    <div class="font-weight-bold">Planungs-Status</div>
                    <div class="text-caption white--text">Muss aktiv sein für Zuweisung</div>
                  </div>
                  <v-switch
                    v-model="emp.planningSettings.isReadyForPlanning"
                    color="success"
                    hide-details
                    class="mt-0"
                    @change="saveSettings(emp)"></v-switch>
                </v-card>

                <div class="mb-4 pa-2 rounded border">
                  <v-switch
                    v-model="emp.isApprentice"
                    label="Ist Lehrling / Auszubildender"
                    dense
                    color="blue darken-2"
                    hide-details
                    class="mt-0"
                    @change="updateEmployeeStatus(emp)"></v-switch>
                  <v-expand-transition>
                    <div v-if="emp.isApprentice" class="mt-3 pl-2 border-left-blue">
                      <div class="text-subtitle-2 blue--text text--darken-2 mb-1">Schultage</div>
                      <v-chip-group
                        multiple
                        column
                        active-class="blue white--text"
                        :value="getSchoolDaysAsArray(emp)"
                        @change="(val) => setSchoolDaysFromArray(emp, val)">
                        <v-chip small filter :value="1">Mo</v-chip>
                        <v-chip small filter :value="2">Di</v-chip>
                        <v-chip small filter :value="3">Mi</v-chip>
                        <v-chip small filter :value="4">Do</v-chip>
                        <v-chip small filter :value="5">Fr</v-chip>
                      </v-chip-group>
                      <v-divider class="my-2"></v-divider>
                      <v-switch
                        v-model="emp.planningSettings.longCommute"
                        label="Langer Schulweg"
                        dense
                        hide-details
                        color="blue"
                        @change="saveSettings(emp)"></v-switch>
                    </div>
                  </v-expand-transition>
                </div>

                <div class="mb-4">
                  <div class="d-flex justify-space-between align-end mb-1">
                    <span class="text-caption white--text font-weight-bold ml-1">
                      <v-icon x-small class="mr-1">mdi-file-document-outline</v-icon>
                      Vertragspensum: {{ getStandardPercentage(emp) }}%
                    </span>
                  </div>
                  <v-text-field
                    label="Manuelles Pensum (%)"
                    v-model.number="emp.planningSettings.percentages[studioId]"
                    type="number"
                    outlined
                    dense
                    suffix="%"
                    prepend-inner-icon="mdi-chart-arc"
                    hint="Nur für diesen Monat"
                    persistent-hint
                    @change="saveSettings(emp)"></v-text-field>

                  <v-expand-transition>
                    <div v-if="getStandardPercentage(emp) <= 40" class="mt-4">
                      <v-switch
                        v-model="emp.planningSettings.groupShifts"
                        label="Schichten in Blöcken gruppieren"
                        dense
                        hide-details
                        color="green"
                        hint="Kleine Pensen: Mitarbeiter nach Möglichkeit an aufeinanderfolgenden Tagen einteilen"
                        persistent-hint
                        @change="saveSettings(emp)"></v-switch>
                    </div>
                  </v-expand-transition>
                </div>

                <div class="mt-6">
                  <div class="text-subtitle-1 font-weight-bold mb-1 d-flex align-center">
                    <v-icon left color="primary">mdi-pin</v-icon> Definitive Zuweisungen
                  </div>
                  <div v-for="day in 7" :key="day" class="d-flex align-center mb-2">
                    <div class="font-weight-medium mr-3" style="width: 100px">{{ getDayName(day) }}</div>
                    <v-select
                      :items="availableShifts"
                      v-model="emp.planningSettings.fixedShifts[day]"
                      item-text="name"
                      item-value="_id"
                      label="Automatisch"
                      dense
                      outlined
                      small-chips
                      clearable
                      hide-details
                      single-line
                      color="primary"
                      placeholder="Keine Vorgabe"
                      @change="saveSettings(emp)">
                      <template v-slot:item="{ item }">
                        <v-chip x-small :color="item.color" class="mr-2"></v-chip>
                        {{ item.name }}
                      </template>
                      <template v-slot:selection="{ item }">
                        <v-chip x-small :color="item.color" class="mr-2"></v-chip>
                        {{ item.name }}
                      </template>
                    </v-select>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6" class="pl-md-4">
                <div class="text-subtitle-1 font-weight-bold mb-3 d-flex align-center">
                  <v-icon left>mdi-calendar-account</v-icon> Abwesenheiten & Wünsche
                </div>

                <v-card outlined class="overflow-hidden">
                  <v-simple-table>
                    <template v-slot:default>
                      <thead class="">
                        <tr>
                          <th class="text-left font-weight-bold">Art</th>
                          <th class="text-left font-weight-bold">Datum</th>
                          <th class="text-left font-weight-bold">Info</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(spec, i) in emp.specialAssignments" :key="'spec' + i" class="hover-row">
                          <td>
                            <v-chip small color="purple lighten-4" text-color="purple darken-4" label class="font-weight-bold">
                              <v-icon left x-small>mdi-star</v-icon> Spezial
                            </v-chip>
                          </td>
                          <td class="font-weight-bold">{{ $moment(spec.date).format("dd, DD.MM") }}</td>
                          <td>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <div v-bind="attrs" v-on="on" class="d-flex align-center cursor-help">
                                  <v-icon small color="purple" class="mr-1">mdi-clock-time-four-outline</v-icon>
                                  <span class="text-caption font-weight-bold purple--text text--darken-2">Fixiert</span>
                                </div>
                              </template>
                              <div class="text-center">
                                <strong>{{ spec.note || "Spezial-Einsatz" }}</strong
                                ><br />
                                <div v-for="(t, ti) in spec.times" :key="ti">{{ t.start }} - {{ t.end }} ({{ t.type }})</div>
                              </div>
                            </v-tooltip>
                          </td>
                        </tr>

                        <tr v-for="(vac, i) in emp.vacations" :key="'vac' + i" class="hover-row">
                          <td>
                            <v-chip small color="blue lighten-4" text-color="blue darken-4" label
                              ><v-icon left x-small>mdi-beach</v-icon> Ferien</v-chip
                            >
                          </td>
                          <td class="font-weight-medium">{{ vac.formattedRange }}</td>
                          <td class="success--text text-caption font-weight-bold"><v-icon x-small color="success">mdi-check</v-icon> Bestätigt</td>
                        </tr>

                        <tr v-for="(fw, i) in emp.freeWishes" :key="'fw' + i" class="hover-row">
                          <td>
                            <v-chip small color="red lighten-4" text-color="red darken-4" label
                              ><v-icon left x-small>mdi-close-circle</v-icon> Frei</v-chip
                            >
                          </td>
                          <td class="font-weight-medium">{{ $moment(fw.day).format("dd, DD.MM") }}</td>
                          <td class="text-caption">Ganzer Tag</td>
                        </tr>

                        <tr v-for="(sw, i) in emp.shiftWishes" :key="'sw' + i" class="hover-row">
                          <td>
                            <v-chip small color="green lighten-4" text-color="green darken-4" label
                              ><v-icon left x-small>mdi-clock-outline</v-icon> Wunsch</v-chip
                            >
                          </td>
                          <td class="font-weight-medium">{{ $moment(sw.day).format("dd, DD.MM") }}</td>
                          <td class="text-caption font-weight-bold">{{ getShiftTypeName(sw.type) }}</td>
                        </tr>

                        <tr v-if="!emp.vacations.length && !emp.freeWishes.length && !emp.shiftWishes.length && !emp.specialAssignments.length">
                          <td colspan="3" class="text-center white--text py-6">
                            <v-icon color="grey lighten-1" large>mdi-calendar-check</v-icon>
                            <div class="mt-2">Keine Einträge diesen Monat</div>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card>

                <div class="mt-6">
                  <div class="text-subtitle-2 mb-2 white--text text--darken-2">Wiederkehrende Sperrtage</div>
                  <v-chip-group multiple v-model="emp.planningSettings.recurringAbsence" column @change="saveSettings(emp)">
                    <v-chip filter outlined color="error" :value="1">Mo</v-chip>
                    <v-chip filter outlined color="error" :value="2">Di</v-chip>
                    <v-chip filter outlined color="error" :value="3">Mi</v-chip>
                    <v-chip filter outlined color="error" :value="4">Do</v-chip>
                    <v-chip filter outlined color="error" :value="5">Fr</v-chip>
                    <v-chip filter outlined color="error" :value="6">Sa</v-chip>
                    <v-chip filter outlined color="error" :value="7">So</v-chip>
                  </v-chip-group>
                </div>

                <div class="mt-6">
                  <div class="text-subtitle-2 mb-2 blue--text text--lighten-2">Tage in anderem Studio (Sollstunden-Abzug)</div>
                  <v-chip-group multiple v-model="emp.planningSettings.recurringExternalStudio" column @change="saveSettings(emp)">
                    <v-chip filter outlined color="blue" :value="1">Mo</v-chip>
                    <v-chip filter outlined color="blue" :value="2">Di</v-chip>
                    <v-chip filter outlined color="blue" :value="3">Mi</v-chip>
                    <v-chip filter outlined color="blue" :value="4">Do</v-chip>
                    <v-chip filter outlined color="blue" :value="5">Fr</v-chip>
                    <v-chip filter outlined color="blue" :value="6">Sa</v-chip>
                    <v-chip filter outlined color="blue" :value="7">So</v-chip>
                  </v-chip-group>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <!-- Bottom Bar -->
    <div class="emp-bottom">
      <div class="emp-bottom-inner">
        <!-- Coverage Bar -->
        <div class="emp-coverage" @click="showCalcDialog = true">
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="text-caption font-weight-bold" style="color: rgba(255, 255, 255, 0.5)"> Abdeckung </span>
            <span class="text-caption font-weight-bold" :class="hourDiff >= 0 ? 'success--text' : 'error--text'">
              {{ Math.round(totalSupplyHours) }}h / {{ Math.round(totalDemandHours) }}h ({{ hourDiff > 0 ? "+" : "" }}{{ Math.round(hourDiff) }}h)
              <v-icon x-small color="grey" class="ml-1">mdi-information-outline</v-icon>
            </span>
          </div>
          <div class="emp-progress-wrap">
            <div class="emp-progress-bg">
              <div
                class="emp-progress-fill"
                :class="coveragePercentage >= 100 ? 'complete' : coveragePercentage >= 80 ? 'partial' : 'low'"
                :style="{ width: Math.min(coveragePercentage, 100) + '%' }"></div>
            </div>
            <span
              class="text-caption font-weight-bold ml-2"
              :class="coveragePercentage >= 100 ? 'success--text' : 'warning--text'"
              style="min-width: 36px"
              >{{ Math.round(coveragePercentage) }}%</span
            >
          </div>
        </div>

        <v-btn depressed @click="triggerAssignment" :disabled="hourDiff < -50" class="emp-submit-btn">
          <v-icon left small>mdi-auto-fix</v-icon> Plan erstellen
        </v-btn>
      </div>
    </div>

    <v-dialog v-model="showCalcDialog" max-width="700">
      <v-card>
        <v-card-title>
          Bedarfs-Berechnung
          <v-spacer></v-spacer>
          <v-btn icon @click="showCalcDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" md="6" class="border-right-custom">
              <div class="text-h6 mb-2">Bedarf (Soll)</div>
              <div class="text-h4 font-weight-bold mb-4">{{ Math.round(totalDemandHours) }}h</div>

              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td>Standard-Monat</td>
                    <td class="text-right">{{ calcStats.standardHours.toFixed(1) }}h</td>
                  </tr>
                  <tr v-if="calcStats.removedHours > 0">
                    <td class="orange--text text--darken-2">Entfall durch Events (Override)</td>
                    <td class="text-right orange--text text--darken-2">- {{ calcStats.removedHours.toFixed(1) }}h</td>
                  </tr>
                  <tr v-if="calcStats.specialHours > 0">
                    <td class="blue--text text--darken-2">Spezial- & Zusatzschichten</td>
                    <td class="text-right blue--text text--darken-2">+ {{ calcStats.specialHours.toFixed(1) }}h</td>
                  </tr>
                </tbody>
              </v-simple-table>

              <div v-if="calcStats.specialEvents.length > 0" class="mt-4">
                <div class="text-caption font-weight-bold white--text mb-1">GEPLANTE SPEZIAL-EVENTS</div>
                <div v-for="(evt, i) in calcStats.specialEvents" :key="i" class="d-flex justify-space-between text-caption mb-1">
                  <span>{{ $moment(evt.date).format("DD.MM") }}: {{ evt.shifts }} Schichten</span>
                  <v-chip x-small label :color="evt.mode === 'replace' ? 'orange lighten-4' : 'blue lighten-4'">
                    {{ evt.mode === "replace" ? "Ersatz" : "Zusatz" }}
                  </v-chip>
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="text-h6 mb-2">Kapazität (Haben)</div>
              <div class="text-h4 font-weight-bold mb-4" :class="hourDiff >= 0 ? 'success--text' : 'error--text'">
                {{ Math.round(totalSupplyHours) }}h
              </div>
              <div class="text-body-2 white--text mb-2">Summe aller Mitarbeiter-Sollstunden abzüglich Schule.</div>
              <v-divider class="mb-2"></v-divider>
              <div class="d-flex justify-space-between text-body-2">
                <span>Differenz:</span>
                <strong :class="hourDiff >= 0 ? 'success--text' : 'error--text'"> {{ hourDiff > 0 ? "+" : "" }}{{ Math.round(hourDiff) }}h </strong>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "ShiftManagementEmployeeSettings",
  props: {
    studioId: { type: String, required: true },
    planningStartDate: { type: String, required: true },
    planningEndDate: { type: String, required: true },
    weeklyTemplateData: { type: Array, default: () => [] },
    availableShifts: { type: Array, default: () => [] },
    schoolHolidays: { type: Array, default: () => [] },
    confirmedEmployeeSettings: { type: Array, default: () => [] },
    handleAssignmentComplete: { type: Function, default: null },
  },
  data() {
    return {
      employees: [],
      specialShifts: [],
      publicHolidays: [], // NEU: Feiertage
      loading: false,
      assigning: false,
      openPanels: [],
      totalDemandHours: 0,
      showCalcDialog: false,
      calcStats: {
        standardHours: 0,
        removedHours: 0,
        specialHours: 0,
        specialEvents: [],
      },
    }
  },
  computed: {
    monthName() {
      return this.planningStartDate ? this.$moment(this.planningStartDate).format("MMMM YYYY") : ""
    },
    readyCount() {
      return this.employees.filter((e) => e.planningSettings.isReadyForPlanning).length
    },
    // NEU: Feiertage die Mo-Fr sind (relevant für Sollstunden)
    publicHolidaysInRange() {
      return this.publicHolidays.filter((h) => !h.isWeekend)
    },
    totalSupplyHours() {
      return this.employees.reduce((sum, emp) => {
        if (!emp.planningSettings.isReadyForPlanning) return sum
        return sum + this.calculateShiftCapacity(emp)
      }, 0)
    },
    hourDiff() {
      return this.totalSupplyHours - this.totalDemandHours
    },
    coveragePercentage() {
      if (this.totalDemandHours === 0) return 100
      const p = (this.totalSupplyHours / this.totalDemandHours) * 100
      return p > 100 ? 100 : p
    },
    coverageColor() {
      if (this.coveragePercentage < 80) return "error"
      if (this.coveragePercentage < 100) return "warning"
      return "success"
    },
  },
  watch: {
    studioId: { immediate: true, handler: "fetchDataAndCalculateTarget" },
    weeklyTemplateData: { immediate: true, handler: "calculateDemand" },
  },
  methods: {
    // NEU: Spezialtage beim Laden mit aufbereiten
    async fetchDataAndCalculateTarget() {
      if (!this.studioId || !this.planningStartDate || !this.planningEndDate) return

      this.loading = true
      try {
        const [allEmployees, planRes, publicHolidaysRes] = await Promise.all([
          api.shiftManagement.fetchShiftManagementEmployee(this.studioId),
          api.shiftManagement.fetchPlan(this.studioId, this.planningStartDate, this.planningEndDate),
          api.shiftManagement.fetchPublicHolidays(this.studioId, this.planningStartDate, this.planningEndDate),
        ])

        // Feiertage speichern
        this.publicHolidays = publicHolidaysRes || []

        // Spezialschichten laden
        const allShifts = planRes.shifts || planRes || []
        // Filtern nach Spezial
        this.specialShifts = allShifts.filter((s) => s.isSpecialDemand === true || (s.allocationNote && s.allocationNote.includes("Spezialtag")))

        this.employees = allEmployees
          .filter((emp) => {
            const studioInfo = emp.studio?.find((s) => String(s._id) === String(this.studioId))
            const basePercentage = emp.timeTrackerPercentage ?? studioInfo?.percentage ?? 0
            return !!studioInfo && basePercentage > 0
          })
          .map((emp) => {
            // NEU: Settings aus Backend laden (shiftPlanningSettings am Employee)
            const backendSettings = (emp.shiftPlanningSettings && emp.shiftPlanningSettings[this.studioId]) || {}
            // Fallback: localStorage für Migration
            const storageKey = `planningSettings_${emp._id}_${this.studioId}`
            const localSettings = JSON.parse(localStorage.getItem(storageKey)) || {}
            // Backend hat Priorität, Fallback auf localStorage
            const savedSettings = Object.keys(backendSettings).length > 0 ? backendSettings : localSettings

            const planningSettings = {
              percentages: savedSettings.percentages || {},
              recurringAbsence: savedSettings.recurringAbsence || [],
              recurringExternalStudio: savedSettings.recurringExternalStudio || [],
              isReadyForPlanning: savedSettings.isReadyForPlanning === true,
              longCommute: savedSettings.longCommute || false,
              fixedShifts: savedSettings.fixedShifts || {},
            }

            const vacations = this.filterByDate(emp.holidayEntries, "dates")
            const freeWishes = this.filterByDateSingle(emp.freeWishEntries, "day")
            const shiftWishes = this.filterByDateSingle(emp.shiftWishEntries, "day")

            // NEU: Spezialschichten für diesen Mitarbeiter sammeln
            const specialAssignments = this.specialShifts
              .filter((s) => {
                const assignedId = s.assignedEmployee ? s.assignedEmployee._id || s.assignedEmployee : null
                // Sicherstellen dass wir IDs vergleichen
                const empUserId = emp.user._id || emp.user
                return String(assignedId) === String(empUserId)
              })
              .sort((a, b) => new Date(a.date) - new Date(b.date))

            return {
              ...emp,
              vacations: vacations.map((h) => ({
                ...h,
                formattedRange:
                  h.dates && h.dates.length
                    ? `${this.$moment(h.dates[0].date).format("DD.MM")} - ${this.$moment(h.dates[h.dates.length - 1].date).format("DD.MM")}`
                    : "",
              })),
              freeWishes,
              shiftWishes,
              specialAssignments, // <--- Hinzugefügt
              planningSettings,
            }
          })

        this.calculateDemand()
      } catch (error) {
        console.error("Fehler beim Laden:", error)
        this.$toast.error("Daten konnten nicht vollständig geladen werden.")
      } finally {
        this.loading = false
      }
    },

    getDayName(iso) {
      const days = ["-", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"]
      return days[iso]
    },
    getShiftTypeName(type) {
      const map = { morning: "Früh", intermediate: "Mittel", late: "Spät" }
      return map[type] || type
    },
    getStandardPercentage(emp) {
      const studioInfo = emp.studio?.find((s) => String(s._id) === String(this.studioId))
      return emp.timeTrackerPercentage ?? studioInfo?.percentage ?? 0
    },

    getSchoolDaysAsArray(emp) {
      if (!emp.schoolDays) return []
      return emp.schoolDays.map((sd) => sd.dayOfWeek)
    },
    setSchoolDaysFromArray(emp, valArray) {
      emp.schoolDays = valArray.map((day) => ({ dayOfWeek: day }))
      this.updateEmployeeStatus(emp)
    },

    // ... (Filter Methoden bleiben gleich) ...
    filterByDate(entries, dateFieldArrayName) {
      if (!entries) return []
      return entries.filter((e) => {
        if (e.status && e.status !== "accepted") return false
        const dates = e[dateFieldArrayName]
        if (!dates || !dates.length) return false
        const start = this.$moment(dates[0].date)
        const end = this.$moment(dates[dates.length - 1].date)
        return start.isSameOrBefore(this.planningEndDate) && end.isSameOrAfter(this.planningStartDate)
      })
    },
    filterByDateSingle(entries, dateFieldName) {
      if (!entries) return []
      return entries.filter((e) => {
        if (e.status && e.status !== "accepted") return false
        const date = this.$moment(e[dateFieldName])
        return date.isBetween(this.planningStartDate, this.planningEndDate, null, "[]")
      })
    },

    async saveSettings(emp) {
      // NEU: Ins Backend speichern statt nur localStorage
      try {
        await api.shiftManagement.savePlanningSettings(emp._id, this.studioId, emp.planningSettings)
        // Auch noch in localStorage als Backup
        const storageKey = `planningSettings_${emp._id}_${this.studioId}`
        localStorage.setItem(storageKey, JSON.stringify(emp.planningSettings))
      } catch (e) {
        console.error("Fehler beim Speichern der Planning-Settings:", e)
        // Fallback: Nur localStorage
        const storageKey = `planningSettings_${emp._id}_${this.studioId}`
        localStorage.setItem(storageKey, JSON.stringify(emp.planningSettings))
      }
      this.employees = [...this.employees]
    },

    async updateEmployeeStatus(emp) {
      try {
        const payload = {
          isApprentice: emp.isApprentice,
          schoolDays: emp.schoolDays || [],
          cannotOpenOrCloseShift: emp.cannotOpenOrCloseShift || false,
        }
        await api.shiftManagement.updateEmployeePlanningSettings(emp._id, payload)
        this.$toast.success("Mitarbeiter-Status aktualisiert")

        if (!emp.isApprentice) {
          emp.planningSettings.longCommute = false
          this.saveSettings(emp)
        }
        this.employees = [...this.employees]
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Speichern des Status")
      }
    },

    markAllReady() {
      this.employees.forEach((e) => {
        e.planningSettings.isReadyForPlanning = true
        this.saveSettings(e)
      })
    },

    calculateContractTarget(emp) {
      if (!emp.planningSettings) return 0
      const perc = emp.planningSettings.percentages[this.studioId] || this.getStandardPercentage(emp)

      let businessDays = 0
      const start = this.$moment(this.planningStartDate)
      const end = this.$moment(this.planningEndDate)
      let current = start.clone()

      const publicHolidayDates = new Set(this.publicHolidaysInRange.map((h) => h.date))

      while (current.isSameOrBefore(end)) {
        const dayOfWeekISO = current.isoWeekday()
        const isWeekend = dayOfWeekISO >= 6
        const dateStr = current.format("YYYY-MM-DD")
        const isPublicHoliday = publicHolidayDates.has(dateStr) && !isWeekend

        const isSchoolHoliday = (this.schoolHolidays || []).some(
          (sh) => sh.startDate && sh.endDate && current.isBetween(sh.startDate, sh.endDate, null, "[]"),
        )

        let isSchoolDay = false
        if (emp.isApprentice && !isSchoolHoliday) {
          isSchoolDay = (emp.schoolDays || []).some((sd) => sd.dayOfWeek === dayOfWeekISO)
        }

        let isWorkDay = !isWeekend && !isSchoolDay && !isPublicHoliday
        if (emp.isApprentice && isSchoolHoliday) isWorkDay = false

        if (isWorkDay) businessDays++

        // Anderes Studio abziehen (immer, auch am Wochenende!)
        if ((emp.planningSettings.recurringExternalStudio || []).includes(dayOfWeekISO)) {
          businessDays--
        }
        current.add(1, "day")
      }

      return businessDays * 8.4 * (perc / 100)
    },

    calculateVacationHours(emp) {
      const perc = emp.planningSettings?.percentages[this.studioId] || this.getStandardPercentage(emp)
      let vacationDays = 0
      const start = this.$moment(this.planningStartDate)
      const end = this.$moment(this.planningEndDate)
      const publicHolidayDates = new Set(this.publicHolidaysInRange.map((h) => h.date))

      for (let vacationDay of emp.vacations) {
        for (let dateEntry of vacationDay.dates) {
          const vacDate = this.$moment(dateEntry.date)
          const vacDateStr = vacDate.format("YYYY-MM-DD")
          if (vacDate.isBetween(start, end, null, "[]") && vacDate.isoWeekday() <= 5 && !publicHolidayDates.has(vacDateStr)) {
            vacationDays++
          }
        }
      }
      return vacationDays * 8.4 * (perc / 100)
    },

    calculateShiftCapacity(emp) {
      const contractTarget = this.calculateContractTarget(emp)
      const vacationHours = this.calculateVacationHours(emp)
      return Math.max(0, contractTarget - vacationHours)
    },

    calculateSchoolHours(emp) {
      if (!emp.isApprentice || !emp.schoolDays || emp.schoolDays.length === 0) return 0

      let schoolDaysCount = 0
      const start = this.$moment(this.planningStartDate)
      const end = this.$moment(this.planningEndDate)
      let current = start.clone()

      while (current.isSameOrBefore(end)) {
        const dayIso = current.isoWeekday()
        const isSchoolDay = emp.schoolDays.some((sd) => sd.dayOfWeek === dayIso)
        const isHoliday = (this.schoolHolidays || []).some(
          (sh) => sh.startDate && sh.endDate && current.isBetween(sh.startDate, sh.endDate, null, "[]"),
        )

        if (isSchoolDay && !isHoliday) {
          schoolDaysCount++
        }
        current.add(1, "day")
      }

      return schoolDaysCount * 8.4
    },

    calculateIndividualTarget(emp) {
      return this.calculateContractTarget(emp)
    },

    calculateDemand() {
      if (!this.weeklyTemplateData || this.weeklyTemplateData.length === 0) {
        this.totalDemandHours = 0
        return
      }

      this.calcStats = { standardHours: 0, removedHours: 0, specialHours: 0, specialEvents: [] }

      const specialDaysMap = {}
      this.specialShifts.forEach((s) => {
        if (!specialDaysMap[s.date]) {
          const isReplace = s.allocationNote && s.allocationNote.includes("Ersatz")
          specialDaysMap[s.date] = { mode: isReplace ? "replace" : "add", shifts: [] }
        }
        specialDaysMap[s.date].shifts.push(s)
      })

      let totalHours = 0
      const start = this.$moment(this.planningStartDate)
      const end = this.$moment(this.planningEndDate)

      let current = start.clone()
      while (current.isSameOrBefore(end, "day")) {
        const dateStr = current.format("YYYY-MM-DD")
        const dayOfWeekISO = current.isoWeekday()

        const specialConfig = specialDaysMap[dateStr]
        const isReplace = specialConfig && specialConfig.mode === "replace"

        const dayTemplate = this.weeklyTemplateData.find((d) => d.dayOfWeek === dayOfWeekISO)
        let dailyStandardHours = 0

        if (dayTemplate && dayTemplate.requiredShifts) {
          dayTemplate.requiredShifts.forEach((req) => {
            const tpl = req.shiftTemplateId
            if (tpl && tpl.times) {
              tpl.times.forEach((time) => {
                if (time.type === "Arbeitszeit" && time.start && time.end) {
                  const s = this.$moment(time.start, "HH:mm")
                  const e = this.$moment(time.end, "HH:mm")
                  if (e.isBefore(s)) e.add(1, "day")
                  dailyStandardHours += e.diff(s, "hours", true)
                }
              })
            }
          })
        }

        if (isReplace) {
          this.calcStats.removedHours += dailyStandardHours
        } else {
          this.calcStats.standardHours += dailyStandardHours
          totalHours += dailyStandardHours
        }

        if (specialConfig) {
          let dailySpecialHours = 0
          specialConfig.shifts.forEach((shift) => {
            const times = shift.times || []
            times.forEach((time) => {
              if (time.type === "Arbeitszeit" && time.start && time.end) {
                const s = this.$moment(time.start, "HH:mm")
                const e = this.$moment(time.end, "HH:mm")
                if (e.isBefore(s)) e.add(1, "day")
                dailySpecialHours += e.diff(s, "hours", true)
              }
            })
          })

          this.calcStats.specialHours += dailySpecialHours
          totalHours += dailySpecialHours

          if (!this.calcStats.specialEvents.find((e) => e.date === dateStr)) {
            this.calcStats.specialEvents.push({
              date: dateStr,
              mode: specialConfig.mode,
              shifts: specialConfig.shifts.length,
            })
          }
        }

        current.add(1, "day")
      }
      this.totalDemandHours = totalHours
    },
    // Ersetze die komplette Methode 'triggerAssignment' hiermit:
    triggerAssignment() {
      // 1. Lokale Speicherung sicherstellen (für jeden Mitarbeiter)
      this.employees.forEach((emp) => {
        this.saveSettings(emp)
      })

      // 2. Prüfung: Sind alle bereit?
      const status = this.getConfirmationStatus()
      if (!status.allMarked) {
        // Falls nicht alle bereit sind, warnen wir kurz, lassen es aber zu
        // (der Parent Dialog zeigt dann meist noch den Bestätigungs-Dialog)
        this.$toast.info("Hinweis: Nicht alle Mitarbeiter sind auf 'Bereit' gesetzt.")
      }

      // 3. Signal an Parent senden: "Weiter zu Schritt 4"
      // Wir rufen keine API mehr hier auf! Das macht der 'Assignment'-Schritt.
      this.$emit("next")
    },
    getConfirmationStatus() {
      const notReady = this.employees.filter((e) => !e.planningSettings.isReadyForPlanning)
      return {
        allMarked: notReady.length === 0,
        notReadyNames: notReady.map((e) => e.fullName),
        readyEmployeeIds: this.employees.filter((e) => e.planningSettings.isReadyForPlanning).map((e) => e.user),
      }
    },
  },
}
</script>

<style scoped>
.emp-settings-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}
.emp-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.emp-header-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  display: flex;
  align-items: center;
  justify-content: center;
}
.emp-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}
.emp-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
}
.emp-panels {
  background: transparent !important;
}
.emp-panel {
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 12px !important;
  margin-bottom: 8px !important;
  overflow: hidden;
  transition: all 0.3s ease;
}
.emp-panel:hover {
  border-color: rgba(255, 255, 255, 0.12) !important;
  background: rgba(255, 255, 255, 0.05) !important;
}
.emp-bottom {
  flex-shrink: 0;
  padding: 12px 20px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(12px);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.emp-bottom-inner {
  display: flex;
  align-items: center;
  gap: 20px;
}
.emp-coverage {
  flex: 1;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 8px;
  transition: background 0.2s;
}
.emp-coverage:hover {
  background: rgba(255, 255, 255, 0.05);
}
.emp-progress-wrap {
  display: flex;
  align-items: center;
}
.emp-progress-bg {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}
.emp-progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.6s ease;
}
.emp-progress-fill.complete {
  background: linear-gradient(90deg, #43a047, #66bb6a);
}
.emp-progress-fill.partial {
  background: linear-gradient(90deg, #fb8c00, #ffa726);
}
.emp-progress-fill.low {
  background: linear-gradient(90deg, #e53935, #ef5350);
}
.emp-submit-btn {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  padding: 0 24px !important;
  box-shadow: 0 4px 16px rgba(76, 175, 80, 0.3) !important;
  flex-shrink: 0;
}
.border-right-custom {
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}
@media (max-width: 960px) {
  .border-right-custom {
    border-right: none;
  }
  .emp-bottom-inner {
    flex-direction: column;
    gap: 12px;
  }
}
.hover-card:hover {
  border-color: #4caf50 !important;
}
.hover-row:hover {
  background-color: rgba(255, 255, 255, 0.04);
}
.border-top {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.border-left-blue {
  border-left: 3px solid #2196f3;
}
.hover-bg:hover {
  background-color: rgba(255, 255, 255, 0.04);
}
.cursor-pointer {
  cursor: pointer;
}
.cursor-help {
  cursor: help;
}
</style>
